import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import NumberFormat from 'react-number-format'
import adminService from '../../services/adminService'
import PageTitle from '../../components/Common/PageTitle'
import Skeleton from 'react-loading-skeleton'
import { toast } from 'react-toastify'
import { toCsv, downloadCsv } from '../../utils/csv'

const AdminSubscriptionManagement = () => {
  const navigate = useNavigate()
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({})
  const [filters, setFilters] = useState({
    status: '',
    planId: '',
    page: 1
  })
  const [subscriptionPlans, setSubscriptionPlans] = useState({})
  const [revenueStats, setRevenueStats] = useState(null)
  const [showPlanEditor, setShowPlanEditor] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [paymentsResponse, plans, dashboardStats] = await Promise.all([
        adminService.getPayments({
          page: filters.page,
          limit: 20,
          status: filters.status,
          planId: filters.planId
        }),
        adminService.getSubscriptionPlans(),
        adminService.getDashboardStats()
      ])
      
      setPayments(paymentsResponse.payments)
      setPagination(paymentsResponse)
      setSubscriptionPlans(plans)
      setRevenueStats({
        totalRevenue: dashboardStats.totalRevenue,
        monthlyRevenue: dashboardStats.monthlyRevenue,
        subscriptionBreakdown: dashboardStats.subscriptionBreakdown,
        activePaidSubscriptions: dashboardStats.activePaidSubscriptions
      })
    } catch (error) {
      console.error('Error loading subscription data:', error)
      toast.error('Failed to load subscription data')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    // Check if user is admin
    if (!adminService.isAdmin()) {
      toast.error('Access denied. Admin privileges required.')
      navigate('/dashboards')
      return
    }

    loadData()
  }, [navigate, loadData])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 when other filters change
    }))
  }

  const handleExportPayments = async () => {
    try {
      const allPayments = await adminService.getPayments({
        page: 1,
        limit: 1000
      })

      const headers = ['Date', 'User Name', 'User Email', 'Plan', 'Billing Cycle', 'Amount', 'Currency', 'Status', 'Payment Method']
      const rows = allPayments.payments.map(payment => [
        new Date(payment.created_at).toLocaleDateString(),
        payment.users?.name || 'N/A',
        payment.users?.email || 'N/A',
        payment.plan_id,
        payment.billing_cycle,
        payment.amount,
        payment.currency,
        payment.status,
        payment.payment_method || 'UPI'
      ])

      downloadCsv(
        `payments-export-${new Date().toISOString().split('T')[0]}.csv`,
        toCsv(headers, rows)
      )

      toast.success('Payments data exported successfully')
    } catch (error) {
      console.error('Error exporting payments:', error)
      toast.error('Failed to export payments data')
    }
  }

  const handleEditPlan = (planKey, plan) => {
    setEditingPlan({ key: planKey, ...plan })
    setShowPlanEditor(true)
  }

  if (loading && payments.length === 0) {
    return (
      <div className="p-4">
        <PageTitle title="Subscription Management" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm border">
              <Skeleton height={20} className="mb-2" />
              <Skeleton height={32} className="mb-4" />
              <Skeleton count={5} height={16} className="mb-1" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <PageTitle title="Subscription Management" />
        <div className="flex space-x-2">
          <button
            onClick={handleExportPayments}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
          >
            Export Payments
          </button>
          <button
            onClick={loadData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Revenue Statistics */}
      {revenueStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Revenue</h3>
            <p className="text-3xl font-bold text-green-600">
              <NumberFormat
                value={revenueStats.totalRevenue}
                displayType="text"
                thousandSeparator={true}
                prefix="₹"
                renderText={(value) => value}
              />
            </p>
            <p className="text-sm text-gray-500">All time earnings</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly Revenue</h3>
            <p className="text-3xl font-bold text-blue-600">
              <NumberFormat
                value={revenueStats.monthlyRevenue}
                displayType="text"
                thousandSeparator={true}
                prefix="₹"
                renderText={(value) => value}
              />
            </p>
            <p className="text-sm text-gray-500">This month</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Active Subscriptions</h3>
            <p className="text-3xl font-bold text-purple-600">
              {revenueStats.activePaidSubscriptions ?? 0}
            </p>
            <p className="text-sm text-gray-500">Paid &amp; active</p>
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Subscription Plans</h2>
          <button
            onClick={() => {
              setEditingPlan({ key: null, isNew: true })
              setShowPlanEditor(true)
            }}
            className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 text-sm"
          >
            + Add plan
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(subscriptionPlans).map(([planKey, plan]) => (
            <PlanCard
              key={planKey}
              planKey={planKey}
              plan={plan}
              userCount={revenueStats?.subscriptionBreakdown?.[planKey] || 0}
              onEdit={() => handleEditPlan(planKey, plan)}
            />
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              value={filters.planId}
              onChange={(e) => handleFilterChange('planId', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Plans</option>
              {Object.keys(subscriptionPlans).filter(key => key !== 'free').map(planKey => (
                <option key={planKey} value={planKey}>
                  {subscriptionPlans[planKey].name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: '', planId: '', page: 1 })}
              className="w-full p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{payment.users?.name || 'Unknown User'}</div>
                    <div className="text-sm text-gray-500">{payment.users?.email || 'No email'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      payment.plan_id === 'pro'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {payment.plan_id}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">{payment.billing_cycle}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <NumberFormat
                      value={payment.amount}
                      displayType="text"
                      thousandSeparator={true}
                      prefix="₹"
                      renderText={(value) => value}
                    />
                    <div className="text-xs text-gray-500">{payment.currency}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      payment.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : payment.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{new Date(payment.created_at).toLocaleDateString()}</div>
                    {payment.completed_at && (
                      <div className="text-xs text-gray-400">
                        Completed: {new Date(payment.completed_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {payment.payment_method || 'UPI'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                disabled={!pagination.hasPrevPage}
                onClick={() => handleFilterChange('page', filters.page - 1)}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={!pagination.hasNextPage}
                onClick={() => handleFilterChange('page', filters.page + 1)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((filters.page - 1) * 20) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(filters.page * 20, pagination.totalCount)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    disabled={!pagination.hasPrevPage}
                    onClick={() => handleFilterChange('page', filters.page - 1)}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    ‹
                  </button>
                  
                  {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                    const page = filters.page <= 3 ? i + 1 : filters.page - 2 + i
                    return (
                      <button
                        key={page}
                        onClick={() => handleFilterChange('page', page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === filters.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  })}

                  <button
                    disabled={!pagination.hasNextPage}
                    onClick={() => handleFilterChange('page', filters.page + 1)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    ›
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plan Editor Modal */}
      {showPlanEditor && editingPlan && (
        <PlanEditorModal
          plan={editingPlan}
          onClose={() => {
            setShowPlanEditor(false)
            setEditingPlan(null)
          }}
          onSaved={() => {
            setShowPlanEditor(false)
            setEditingPlan(null)
            loadData()
          }}
          onDeleted={() => {
            setShowPlanEditor(false)
            setEditingPlan(null)
            loadData()
          }}
        />
      )}
    </div>
  )
}

// Top-level so React keeps the same component identity across parent
// re-renders. When this lived inside AdminSubscriptionManagement, every
// parent re-render created a new component type and the modal remounted
// mid-edit, resetting the form to defaults — exactly the "select something
// then it resets when you tab to the next field" symptom.
const PlanCard = ({ planKey, plan, userCount = 0, onEdit }) => {
  const isPopular = plan.popular

  return (
    <div className={`bg-white rounded-lg border-2 p-6 relative ${
      isPopular ? 'border-blue-500 shadow-lg' : 'border-gray-200 shadow-sm'
    }`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
        <div className="mt-2">
          <span className="text-3xl font-bold text-gray-900">
            {plan.billing?.monthly?.price ? (
              <NumberFormat
                value={plan.billing.monthly.price}
                displayType="text"
                thousandSeparator={true}
                prefix="₹"
                renderText={(value) => value}
              />
            ) : (
              'Free'
            )}
          </span>
          {plan.billing?.monthly && <span className="text-gray-600">/month</span>}
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600">Active Users</span>
          <span className="font-semibold text-gray-900">{userCount}</span>
        </div>
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Features</h4>
          <ul className="space-y-1">
            {(plan.features || []).slice(0, 5).map((feature, index) => (
              <li key={index} className="text-xs text-gray-600 flex items-center">
                <svg className="w-3 h-3 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="text-xs text-gray-500 space-y-1">
          <div>Invoices: {plan.limitations?.invoicesSaveExport === -1 ? 'Unlimited' : plan.limitations?.invoicesSaveExport}</div>
          <div>Clients: {plan.limitations?.clients === -1 ? 'Unlimited' : plan.limitations?.clients}</div>
          <div>Products: {plan.limitations?.products === -1 ? 'Unlimited' : plan.limitations?.products}</div>
        </div>
      </div>

      <button
        onClick={onEdit}
        className="w-full mt-4 bg-gray-100 text-gray-700 py-2 rounded-md hover:bg-gray-200 text-sm font-medium"
      >
        Edit Plan
      </button>
    </div>
  )
}

const PLAN_CYCLE_KEYS = ['monthly', 'quarterly', 'halfYearly', 'annual']
const PLAN_CYCLE_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly (3 months)',
  halfYearly: 'Half-yearly (6 months)',
  annual: 'Annual'
}
const PLAN_CYCLE_INTERVALS = {
  monthly: 'month',
  quarterly: '3 months',
  halfYearly: '6 months',
  annual: 'year'
}
// Months in each cycle — used to derive non-monthly prices from monthly.
const PLAN_CYCLE_MONTHS = {
  monthly: 1,
  quarterly: 3,
  halfYearly: 6,
  annual: 12
}
const PLAN_EXPORT_FORMATS = ['pdf', 'drive', 'csv', 'xlsx', 'json']
const PLAN_TEMPLATES = ['default', 'modern', 'formal', 'custom']

// Derive non-monthly prices from monthly price + per-cycle savings %.
// Formula: price = monthly × months × (1 - savings / 100), rounded to ₹1.
// Returns a fresh `billing` object; falls back to leaving the price as-is
// when monthly price isn't a valid number yet (e.g. mid-typing).
const computeDerivedBilling = (billing) => {
  const monthlyPrice = parseFloat(billing?.monthly?.price)
  const next = { ...billing }
  if (!Number.isFinite(monthlyPrice)) return next

  for (const cycle of ['quarterly', 'halfYearly', 'annual']) {
    if (!next[cycle]) continue
    const savings = parseFloat(next[cycle].savings) || 0
    const months = PLAN_CYCLE_MONTHS[cycle]
    const computed = Math.round(monthlyPrice * months * (1 - savings / 100))
    next[cycle] = { ...next[cycle], price: computed }
  }
  return next
}

const PlanEditorModal = ({ plan, onClose, onSaved, onDeleted }) => {
  const isNew = !!plan?.isNew

  const seededBilling = PLAN_CYCLE_KEYS.reduce((acc, key) => {
    const existing = plan?.billing?.[key]
    acc[key] = {
      enabled: !!existing,
      price: existing?.price ?? '',
      currency: existing?.currency || 'INR',
      usageLimit: existing?.usageLimit ?? '',
      razorpayPlanId: existing?.razorpayPlanId || '',
      savings: existing?.savings ?? 0
    }
    return acc
  }, {})
  // Recompute derived prices on open so the displayed quarterly / half-yearly /
  // annual values are always in sync with monthly × savings, even if the DB
  // stored a manually-set number from before this auto-calc behaviour shipped.
  const initialBilling = computeDerivedBilling(seededBilling)

  const [form, setForm] = useState({
    id: plan?.key || plan?.id || '',
    name: plan?.name || '',
    popular: plan?.popular || false,
    active: plan?.active !== false,
    sortOrder: plan?.sortOrder ?? 0,
    monthlyInvoiceLimit: plan?.monthlyInvoiceLimit ?? '',
    featuresText: (plan?.features || []).join('\n'),
    invoicesSaveExport: plan?.limitations?.invoicesSaveExport ?? 10,
    clients: plan?.limitations?.clients ?? 5,
    products: plan?.limitations?.products ?? 5,
    exportFormats: plan?.limitations?.exportFormats || ['pdf'],
    autoSyncFrequency: plan?.limitations?.autoSyncFrequency || 'manual',
    templateAccess: plan?.limitations?.templateAccess || ['default'],
    customTemplates: !!plan?.limitations?.customTemplates,
    supportLevel: plan?.limitations?.supportLevel || 'none',
    exportToDrive: !!plan?.limitations?.exportToDrive,
    emailShare: !!plan?.limitations?.emailShare,
    billing: initialBilling
  })
  const [saving, setSaving] = useState(false)

  const toggleArrayValue = (field, value) => {
    setForm(prev => {
      const set = new Set(prev[field])
      if (set.has(value)) set.delete(value)
      else set.add(value)
      return { ...prev, [field]: Array.from(set) }
    })
  }

  const updateBilling = (cycle, field, value) => {
    setForm(prev => {
      const updatedBilling = {
        ...prev.billing,
        [cycle]: { ...prev.billing[cycle], [field]: value }
      }
      // Re-derive non-monthly prices when the inputs that drive them change:
      //   - monthly price: re-derives all three derived cycles
      //   - any cycle's savings %: re-derives that cycle's price
      //   - enabling a non-monthly cycle: fills in its first computed price
      const shouldRecompute =
        (cycle === 'monthly' && field === 'price') ||
        field === 'savings' ||
        (field === 'enabled' && value === true && cycle !== 'monthly')

      const nextBilling = shouldRecompute ? computeDerivedBilling(updatedBilling) : updatedBilling
      return { ...prev, billing: nextBilling }
    })
  }

  const buildPayload = () => {
    const billing = {}
    for (const key of PLAN_CYCLE_KEYS) {
      const c = form.billing[key]
      if (!c.enabled) continue
      const price = parseFloat(c.price)
      if (Number.isNaN(price)) {
        throw new Error(`${PLAN_CYCLE_LABELS[key]}: price is required when the cycle is enabled.`)
      }
      billing[key] = {
        price,
        currency: c.currency || 'INR',
        interval: PLAN_CYCLE_INTERVALS[key],
        usageLimit: c.usageLimit === '' ? null : parseInt(c.usageLimit, 10),
        razorpayPlanId: c.razorpayPlanId || null,
        savings: c.savings === '' ? 0 : parseFloat(c.savings)
      }
    }

    return {
      id: form.id.trim(),
      name: form.name.trim(),
      popular: !!form.popular,
      active: !!form.active,
      sortOrder: parseInt(form.sortOrder, 10) || 0,
      monthlyInvoiceLimit: form.monthlyInvoiceLimit === '' ? null : parseInt(form.monthlyInvoiceLimit, 10),
      features: form.featuresText.split('\n').map(s => s.trim()).filter(Boolean),
      limitations: {
        exportFormats: form.exportFormats,
        autoSyncFrequency: form.autoSyncFrequency,
        templateAccess: form.templateAccess,
        customTemplates: !!form.customTemplates,
        supportLevel: form.supportLevel,
        invoicesSaveExport: parseInt(form.invoicesSaveExport, 10),
        clients: parseInt(form.clients, 10),
        products: parseInt(form.products, 10),
        exportToDrive: !!form.exportToDrive,
        emailShare: !!form.emailShare
      },
      billing
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      const payload = buildPayload()
      await adminService.upsertSubscriptionPlan(payload)
      toast.success(`Plan ${isNew ? 'created' : 'updated'} successfully`)
      onSaved && onSaved()
    } catch (err) {
      toast.error(err.message || 'Failed to save plan')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!plan?.key && !plan?.id) return
    const id = plan.key || plan.id
    if (!window.confirm(`Delete plan "${form.name}"? This cannot be undone.`)) return
    try {
      setSaving(true)
      await adminService.deleteSubscriptionPlan(id)
      toast.success('Plan deleted')
      onDeleted && onDeleted()
    } catch (err) {
      toast.error(err.message || 'Failed to delete plan')
    } finally {
      setSaving(false)
    }
  }

  const labelCls = 'block text-xs font-medium text-gray-700 mb-1'
  const inputCls = 'w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {isNew ? 'Add New Plan' : `Edit Plan: ${plan.name}`}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Plan ID</label>
              <input
                type="text"
                value={form.id}
                onChange={(e) => setForm(prev => ({ ...prev, id: e.target.value }))}
                disabled={!isNew}
                placeholder="e.g. starter"
                className={`${inputCls} ${!isNew ? 'bg-gray-100' : ''}`}
              />
              <p className="text-[11px] text-gray-500 mt-1">Lowercase id, used as the key. Cannot be changed once created.</p>
            </div>
            <div>
              <label className={labelCls}>Display name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Sort order</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm(prev => ({ ...prev, sortOrder: e.target.value }))}
                className={inputCls}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={form.popular}
                onChange={(e) => setForm(prev => ({ ...prev, popular: e.target.checked }))}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
              />
              Mark as popular
            </label>
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
              />
              Active (visible on pricing page)
            </label>
          </div>

          <div className="border rounded-md p-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Billing cycles</h4>
            <p className="text-xs text-gray-500 mb-3">
              Set the <strong>monthly</strong> price and a <strong>savings %</strong> on each
              longer cycle — the price for quarterly, half-yearly and annual is computed as
              <code className="mx-1 px-1 bg-gray-100 rounded text-[11px]">monthly × months × (1 − savings/100)</code>
              and refreshed live as you type. Disable a cycle to remove it from the plan.
            </p>
            <div className="space-y-3">
              {PLAN_CYCLE_KEYS.map(cycle => {
                const c = form.billing[cycle]
                const isDerived = cycle !== 'monthly'
                return (
                  <div key={cycle} className="border border-gray-200 rounded p-3">
                    <label className="flex items-center text-sm font-medium mb-2">
                      <input
                        type="checkbox"
                        checked={c.enabled}
                        onChange={(e) => updateBilling(cycle, 'enabled', e.target.checked)}
                        className="h-4 w-4 mr-2"
                      />
                      {PLAN_CYCLE_LABELS[cycle]}
                    </label>
                    {c.enabled && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div>
                          <label className={labelCls}>
                            Price{isDerived && <span className="ml-1 text-[10px] text-gray-400">(auto)</span>}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={c.price}
                            readOnly={isDerived}
                            onChange={(e) => updateBilling(cycle, 'price', e.target.value)}
                            className={`${inputCls} ${isDerived ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Currency</label>
                          <input type="text" value={c.currency}
                            onChange={(e) => updateBilling(cycle, 'currency', e.target.value)} className={inputCls} />
                        </div>
                        <div>
                          <label className={labelCls}>Usage limit</label>
                          <input type="number" value={c.usageLimit}
                            placeholder="-1 for unlimited"
                            onChange={(e) => updateBilling(cycle, 'usageLimit', e.target.value)} className={inputCls} />
                        </div>
                        <div>
                          <label className={labelCls}>
                            Savings %{!isDerived && <span className="ml-1 text-[10px] text-gray-400">(n/a)</span>}
                          </label>
                          <input
                            type="number"
                            value={isDerived ? c.savings : 0}
                            disabled={!isDerived}
                            onChange={(e) => updateBilling(cycle, 'savings', e.target.value)}
                            className={`${inputCls} ${!isDerived ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Razorpay plan id</label>
                          <input type="text" value={c.razorpayPlanId}
                            onChange={(e) => updateBilling(cycle, 'razorpayPlanId', e.target.value)} className={inputCls} />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="border rounded-md p-4 space-y-4">
            <h4 className="text-sm font-semibold text-gray-800">Limitations</h4>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Invoices save/export</label>
                <input type="number" value={form.invoicesSaveExport}
                  onChange={(e) => setForm(prev => ({ ...prev, invoicesSaveExport: e.target.value }))}
                  className={inputCls} placeholder="-1 for unlimited" />
              </div>
              <div>
                <label className={labelCls}>Clients</label>
                <input type="number" value={form.clients}
                  onChange={(e) => setForm(prev => ({ ...prev, clients: e.target.value }))}
                  className={inputCls} placeholder="-1 for unlimited" />
              </div>
              <div>
                <label className={labelCls}>Products</label>
                <input type="number" value={form.products}
                  onChange={(e) => setForm(prev => ({ ...prev, products: e.target.value }))}
                  className={inputCls} placeholder="-1 for unlimited" />
              </div>
              <div>
                <label className={labelCls}>Monthly invoice limit</label>
                <input type="number" value={form.monthlyInvoiceLimit}
                  onChange={(e) => setForm(prev => ({ ...prev, monthlyInvoiceLimit: e.target.value }))}
                  className={inputCls} placeholder="-1 for unlimited" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Auto-sync frequency</label>
                <select value={form.autoSyncFrequency}
                  onChange={(e) => setForm(prev => ({ ...prev, autoSyncFrequency: e.target.value }))}
                  className={inputCls}>
                  <option value="manual">Manual only</option>
                  <option value="30min">Every 30 minutes</option>
                  <option value="5min">Every 5 minutes</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Support level</label>
                <select value={form.supportLevel}
                  onChange={(e) => setForm(prev => ({ ...prev, supportLevel: e.target.value }))}
                  className={inputCls}>
                  <option value="none">None</option>
                  <option value="email">Email</option>
                  <option value="priority">Priority</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Export formats</label>
              <div className="flex flex-wrap gap-3">
                {PLAN_EXPORT_FORMATS.map(fmt => (
                  <label key={fmt} className="flex items-center text-sm">
                    <input type="checkbox" className="h-4 w-4 mr-1.5"
                      checked={form.exportFormats.includes(fmt)}
                      onChange={() => toggleArrayValue('exportFormats', fmt)} />
                    {fmt}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls}>Template access</label>
              <div className="flex flex-wrap gap-3">
                {PLAN_TEMPLATES.map(tpl => (
                  <label key={tpl} className="flex items-center text-sm">
                    <input type="checkbox" className="h-4 w-4 mr-1.5"
                      checked={form.templateAccess.includes(tpl)}
                      onChange={() => toggleArrayValue('templateAccess', tpl)} />
                    {tpl}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center text-sm">
                <input type="checkbox" className="h-4 w-4 mr-2"
                  checked={form.customTemplates}
                  onChange={(e) => setForm(prev => ({ ...prev, customTemplates: e.target.checked }))} />
                Custom templates
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="h-4 w-4 mr-2"
                  checked={form.exportToDrive}
                  onChange={(e) => setForm(prev => ({ ...prev, exportToDrive: e.target.checked }))} />
                Export to Google Drive
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="h-4 w-4 mr-2"
                  checked={form.emailShare}
                  onChange={(e) => setForm(prev => ({ ...prev, emailShare: e.target.checked }))} />
                Email sharing
              </label>
            </div>
          </div>

          <div>
            <label className={labelCls}>Marketing feature bullets (one per line)</label>
            <textarea
              rows={6}
              value={form.featuresText}
              onChange={(e) => setForm(prev => ({ ...prev, featuresText: e.target.value }))}
              className={inputCls}
              placeholder="50 invoices save & export\n50 clients\n..."
            />
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <div>
              {!isNew && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving}
                  className="px-3 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 text-sm disabled:opacity-50"
                >
                  Delete plan
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : (isNew ? 'Create plan' : 'Save changes')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminSubscriptionManagement